import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "../api.js";
import { apiExportCustomers, apiGetCustomerOrders } from "./customers/customersApi.js";
import AgingReport from "./customers/AgingReport.jsx";
import { useFormValidation } from "../hooks/useFormValidation.js";
import { customerUpdateSchema, CUSTOMER_TYPES } from "../schemas/customer.schema.js";
import { CustomersTable } from "./customers/CustomersTable.jsx";
import {
  Users,
  Search,
  X,
  Download,
  Upload,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Phone,
  Mail,
  Calendar,
  DollarSign,
  Bell,
  Pencil,
  AlertCircle,
  CheckCircle,
  Clock,
} from "lucide-react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function Pill({ children, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-100 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    yellow: "bg-amber-50 text-amber-700 ring-amber-100",
    blue: "bg-sky-50 text-sky-700 ring-sky-100",
    red: "bg-rose-50 text-rose-700 ring-rose-100",
    purple: "bg-violet-50 text-violet-700 ring-violet-100",
    indigo: "bg-slate-50 text-slate-800 ring-slate-200",
    orange: "bg-amber-50 text-amber-800 ring-amber-100",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
        tones[tone] || tones.neutral
      )}
    >
      {children}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "neutral" }) {
  const toneColors = {
    neutral: "text-slate-900",
    green: "text-emerald-600",
    red: "text-rose-600",
    blue: "text-sky-600",
    orange: "text-amber-600",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium text-slate-500">{label}</div>
          <div className={cx("mt-1 text-xl font-bold", toneColors[tone] || toneColors.neutral)}>
            {value}
          </div>
          {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2">
          <Icon className="h-5 w-5 text-slate-700" />
        </div>
      </div>
    </div>
  );
}

function ModalShell({ title, subtitle, onClose, children, size = "lg" }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const sizeClass = size === "xl" ? "max-w-4xl" : "max-w-2xl";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div
        className={cx(
          "relative w-full overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl",
          sizeClass
        )}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <div className="text-lg font-bold text-slate-900">{title}</div>
            {subtitle && <div className="text-xs text-slate-500">{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white p-2 hover:bg-slate-50"
          >
            <X className="h-4 w-4 text-slate-700" />
          </button>
        </div>
        <div className="max-h-[80vh] overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="h-5 w-40 animate-pulse rounded bg-slate-100" />
              <div className="h-3 w-32 animate-pulse rounded bg-slate-100" />
            </div>
            <div className="h-6 w-16 animate-pulse rounded-full bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NewInactiveModal({ open, onClose, items }) {
  if (!open) return null;

  return (
    <ModalShell title="Nouveaux clients inactifs" subtitle="Dernières 24h" onClose={onClose}>
      {!items?.length ? (
        <p className="text-sm text-slate-500">Aucun client devenu inactif recemment.</p>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div
              key={c.uid}
              className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <div>
                <div className="font-semibold text-slate-900">{c.name || "—"}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {c.phone || "—"} · {c.email || "—"}
                </div>
              </div>
              <div className="text-right">
                <Pill tone="red">Inactif</Pill>
                <div className="text-xs text-slate-500 mt-1">
                  {c.became_inactive_at
                    ? new Date(c.became_inactive_at).toLocaleString("fr-FR")
                    : "—"}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </ModalShell>
  );
}

const RFM_SEG_QUICK = {
  champion:   { label: 'Champion',    emoji: '🥇', cls: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
  loyal:      { label: 'Fidèle',      emoji: '🥈', cls: 'bg-blue-50 border-blue-200 text-blue-800'          },
  new:        { label: 'Nouveau',     emoji: '🌱', cls: 'bg-violet-50 border-violet-200 text-violet-800'    },
  at_risk:    { label: 'À risque',    emoji: '⚠️', cls: 'bg-amber-50 border-amber-200 text-amber-800'       },
  occasional: { label: 'Occasionnel', emoji: '🔄', cls: 'bg-slate-100 border-slate-200 text-slate-700'      },
  lost:       { label: 'Perdu',       emoji: '💤', cls: 'bg-rose-50 border-rose-200 text-rose-800'          },
}

function QuickViewModal({ open, onClose, customer, orders, rfm, onCustomerUpdated }) {
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [ordersLimit, setOrdersLimit] = useState(50);

  const { errors, validate, clearErrors, clearFieldError } = useFormValidation(customerUpdateSchema);

  const fmtDate = useMemo(() => new Intl.DateTimeFormat("fr-FR"), []);
  const fmtDateTime = useMemo(
    () => new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }),
    []
  );

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    type: "particulier",
    is_active: true,
  });

  useEffect(() => {
    if (!open || !customer) return;

    setEditMode(false);
    setSaveErr("");
    setSaving(false);
    setOrdersLimit(50);
    clearErrors();

    setForm({
      name: customer.name || "",
      phone: customer.phone || "",
      email: customer.email || "",
      type: customer.type || "particulier",
      is_active: !!customer.is_active,
    });
  }, [open, customer?.uid]);

  const totalCmd = customer?.total_orders ?? (orders?.length || 0);
  const lastDate = customer?.last_purchase_at || orders?.[0]?.created_at;

  const visibleOrders = useMemo(() => {
    const arr = Array.isArray(orders) ? orders : [];
    return arr.slice(0, ordersLimit);
  }, [orders, ordersLimit]);

  const planId = useMemo(() => {
    const arr = Array.isArray(orders) ? orders : [];
    const pick = (o) => o?.plan_id || o?.payment_plan_id || o?.planId || o?.paymentPlanId;
    for (const o of arr) {
      const pid = pick(o);
      if (pid) return pid;
    }
    return null;
  }, [orders]);

  if (!open || !customer) return null;

  const onChange = (k) => (e) => {
    const v = k === "is_active" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) clearFieldError(k);
  };

  const buildPatch = () => {
    const patch = {};
    const trimOrNull = (v) => {
      const s = String(v ?? "").trim();
      return s === "" ? null : s;
    };

    const nextName = trimOrNull(form.name);
    const nextPhone = trimOrNull(form.phone);
    const nextEmail = trimOrNull(form.email);
    const nextType = trimOrNull(form.type) || "particulier";
    const nextActive = !!form.is_active;

    const curName = trimOrNull(customer.name);
    const curPhone = trimOrNull(customer.phone);
    const curEmail = trimOrNull(customer.email);
    const curType = trimOrNull(customer.type) || "particulier";
    const curActive = !!customer.is_active;

    if (nextName !== curName) patch.name = nextName;
    if (nextPhone !== curPhone) patch.phone = nextPhone;
    if (nextEmail !== curEmail) patch.email = nextEmail;
    if (nextType !== curType) patch.type = nextType;
    if (nextActive !== curActive) patch.is_active = nextActive;

    return patch;
  };

  const onSave = async () => {
    setSaving(true);
    setSaveErr("");

    const validation = validate(form);
    if (!validation.success) {
      setSaving(false);
      setSaveErr("Corrigez les erreurs de validation");
      return;
    }

    try {
      const patch = buildPatch();
      if (!Object.keys(patch).length) {
        setSaveErr("Aucune modification detectee.");
        setSaving(false);
        return;
      }

      const updated = await apiFetch(`/api/customers/${customer.uid}`, {
        method: "PUT",
        json: patch,
      });

      setEditMode(false);
      clearErrors();
      onCustomerUpdated?.(updated);
    } catch (e) {
      setSaveErr(e?.message || "Erreur mise a jour client");
    } finally {
      setSaving(false);
    }
  };

  const inputBase =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100";
  const inputError =
    "w-full rounded-2xl border border-rose-300 bg-rose-50 px-4 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-4 focus:ring-rose-100";

  return (
    <ModalShell
      title={`Client · ${customer.name || "Sans nom"}`}
      subtitle={`UID: ${customer.uid}`}
      onClose={onClose}
      size="xl"
    >
      <div className="space-y-6">
        {saveErr && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {saveErr}
          </div>
        )}

        {/* Badges + actions */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            <Pill tone={customer.is_active ? "green" : "red"}>
              {customer.is_active ? "Actif" : "Inactif"}
            </Pill>
            {customer.created_at && (
              <Pill tone="neutral">Cree: {fmtDate.format(new Date(customer.created_at))}</Pill>
            )}
            <Pill tone="indigo">Commandes: {totalCmd}</Pill>
            <Pill tone="neutral">
              Dernier: {lastDate ? fmtDate.format(new Date(lastDate)) : "—"}
            </Pill>
          </div>

          <div className="flex gap-2">
            {!editMode ? (
              <button
                onClick={() => setEditMode(true)}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                <Pencil className="h-4 w-4" />
                Modifier
              </button>
            ) : (
              <>
                <button
                  disabled={saving}
                  onClick={() => {
                    setEditMode(false);
                    setSaveErr("");
                    setForm({
                      name: customer.name || "",
                      phone: customer.phone || "",
                      email: customer.email || "",
                      type: customer.type || "particulier",
                      is_active: !!customer.is_active,
                    });
                  }}
                  className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                >
                  Annuler
                </button>
                <button
                  disabled={saving}
                  onClick={onSave}
                  className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-70"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Info + paiements */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="font-bold text-slate-900 mb-4">Informations</h3>

            {!editMode ? (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-700">{customer.phone || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-700">{customer.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-700">Type: {customer.type || "particulier"}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-500">Nom *</label>
                  <input
                    value={form.name}
                    onChange={onChange("name")}
                    className={errors.name ? inputError : inputBase}
                  />
                  {errors.name && <p className="text-xs text-rose-600 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Telephone</label>
                  <input
                    value={form.phone}
                    onChange={onChange("phone")}
                    className={errors.phone ? inputError : inputBase}
                  />
                  {errors.phone && <p className="text-xs text-rose-600 mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Email</label>
                  <input
                    value={form.email}
                    onChange={onChange("email")}
                    className={errors.email ? inputError : inputBase}
                  />
                  {errors.email && <p className="text-xs text-rose-600 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500">Type</label>
                  <select value={form.type} onChange={onChange("type")} className={inputBase}>
                    {CUSTOMER_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={!!form.is_active}
                    onChange={onChange("is_active")}
                    className="rounded"
                  />
                  <span className="text-sm text-slate-700">Client actif</span>
                </label>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <h3 className="font-bold text-slate-900 mb-4">Paiements differes</h3>

            {customer.has_deferred_payment && customer.next_payment_due_at ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-700">
                    Prochaine echeance:{" "}
                    <strong>{fmtDate.format(new Date(customer.next_payment_due_at))}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="h-4 w-4 text-slate-400" />
                  <span className="text-slate-700">
                    Reste:{" "}
                    <strong>
                      {Number(customer.remaining_total || 0).toLocaleString("fr-FR")} FCFA
                    </strong>
                  </span>
                </div>

                {planId ? (
                  <Link
                    to={`/installments?plan_id=${planId}&customer_uid=${customer.uid}`}
                    className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Calendar className="h-4 w-4" />
                    Voir echeances
                  </Link>
                ) : (
                  <button
                    disabled
                    className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2 text-sm text-slate-400"
                  >
                    Echeances indisponibles
                  </button>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Aucun paiement a echeance.</p>
            )}
          </div>
        </div>

        {/* Bloc RFM */}
        {rfm?.segment && (() => {
          const seg = RFM_SEG_QUICK[rfm.segment] || RFM_SEG_QUICK.occasional
          return (
            <div className={`rounded-2xl border px-4 py-3 ${seg.cls}`}>
              <div className="flex items-center gap-3 flex-wrap text-sm">
                <span className="text-lg">{seg.emoji}</span>
                <span className="font-black">{seg.label}</span>
                <span className="opacity-60 text-xs">R{rfm.r_score}·F{rfm.f_score}·M{rfm.m_score}</span>
                <span className="opacity-40">·</span>
                <span><strong>{rfm.frequency ?? 0}</strong> cmd</span>
                <span className="opacity-40">·</span>
                {rfm.recency_days != null && <>
                  <span>Dernier achat il y a <strong>{rfm.recency_days} j</strong></span>
                  <span className="opacity-40">·</span>
                </>}
                <span><strong>{Number(rfm.monetary ?? 0).toLocaleString('fr-FR')} FCFA</strong> dépensé</span>
              </div>
            </div>
          )
        })()}

        {/* Historique */}
        <div>
          <h3 className="font-bold text-slate-900 mb-4">Historique commandes</h3>

          {!orders?.length ? (
            <p className="text-sm text-slate-500">Aucune commande.</p>
          ) : (
            <>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {visibleOrders.map((o) => (
                  <div
                    key={o.id}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div>
                      <div className="font-semibold text-slate-900">{o.external_id}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        {o.created_at ? fmtDateTime.format(new Date(o.created_at)) : "—"}
                      </div>
                    </div>
                    <div className="font-bold text-slate-900">
                      {Number(o.total_ttc || 0).toLocaleString("fr-FR")} FCFA
                    </div>
                  </div>
                ))}
              </div>

              {orders.length > ordersLimit && (
                <button
                  onClick={() => setOrdersLimit((x) => x + 50)}
                  className="mt-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
                >
                  Afficher +50
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

export default function Customers() {
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState(() => searchParams.get('tab') === 'aging' ? 'aging' : 'list');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('customers_view_mode') || 'grid');

  const [page, setPage] = useState(1);
  const perPage = 50;
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);

  const [rfmMap, setRfmMap] = useState({});

  const [quick, setQuick] = useState({ open: false, customer: null, orders: [], rfm: null });
  const [newInactive, setNewInactive] = useState({ count: 0, customers: [] });
  const [openInactiveModal, setOpenInactiveModal] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    phone: '',
    email: '',
    type: 'particulier',
    is_active: true
  });
  const [createErr, setCreateErr] = useState('');

  const now = Date.now();
  const DAY = 24 * 60 * 60 * 1000;

  const backendSupportedFilters = useMemo(
    () => new Set(["all", "active", "inactive", "top", "recent", "due"]),
    []
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ, filter]);

  function dueTone(nextDueAt) {
    const ts = new Date(nextDueAt).getTime();
    if (Number.isNaN(ts)) return "orange";
    if (ts < now) return "red";
    if (ts - now < 7 * DAY) return "orange";
    return "indigo";
  }

  async function load(currentPage) {
    const p = Math.max(1, Number(currentPage || 1));
    setLoading(true);
    setErrMsg("");

    try {
      const params = new URLSearchParams();
      if (debouncedQ) params.set("search", debouncedQ);
      if (filter && filter !== "all" && backendSupportedFilters.has(filter)) {
        params.set("filter", filter);
      }
      params.set("page", String(p));
      params.set("limit", String(perPage));

      const data = await apiFetch(`/api/customers?${params.toString()}`);

      const list = Array.isArray(data) ? data : data?.rows || [];
      setRows(Array.isArray(list) ? list : []);

      if (!Array.isArray(data)) {
        setTotal(Number(data?.total || 0));
        setPageCount(Math.max(1, Number(data?.pageCount || 1)));
      } else {
        setTotal(list.length);
        setPageCount(1);
      }
    } catch (e) {
      setRows([]);
      setTotal(0);
      setPageCount(1);
      setErrMsg(e?.message || "Erreur chargement clients");
    } finally {
      setLoading(false);
    }
  }

  async function loadNewInactive() {
    try {
      const data = await apiFetch("/api/customers/alerts/new-inactive?hours=24&limit=200");
      setNewInactive({
        count: Number(data?.count || 0),
        customers: Array.isArray(data?.customers) ? data.customers : [],
      });
    } catch (e) {
      setNewInactive({ count: 0, customers: [] });
    }
  }

  useEffect(() => {
    load(page);
  }, [page, debouncedQ, filter]);

  useEffect(() => {
    loadNewInactive();
    const t = setInterval(loadNewInactive, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    apiFetch('/api/analytics/rfm?format=json&limit=2000')
      .then(data => {
        const arr = Array.isArray(data) ? data : (data?.rows || []);
        const map = {};
        arr.forEach(r => { if (r.uid) map[r.uid] = r; });
        setRfmMap(map);
      })
      .catch(() => {});
  }, []);

  const onPage = (p) => {
    const next = Math.min(Math.max(1, Number(p || 1)), pageCount || 1);
    setPage(next);
  };

  const onExport = async () => {
    const blob = await apiExportCustomers();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clients_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    await apiFetch("/api/customers/import", { method: "POST", body: formData });
    setPage(1);
    await load(1);
    await loadNewInactive();
  };

  const openQuick = async (c) => {
    setQuick({ open: true, customer: c, orders: [], rfm: rfmMap[c.uid] || null });

    try {
      const [fresh, data, rfmData] = await Promise.all([
        apiFetch(`/api/customers/${c.uid}`),
        apiGetCustomerOrders(c.uid),
        apiFetch(`/api/analytics/rfm/customer/${c.uid}`).catch(() => null),
      ]);

      setQuick({
        open: true,
        customer: {
          ...fresh,
          has_deferred_payment: fresh?.has_deferred_payment ?? c.has_deferred_payment,
          next_payment_due_at: fresh?.next_payment_due_at ?? c.next_payment_due_at,
          remaining_total: fresh?.remaining_total ?? c.remaining_total,
        },
        orders: data?.orders || [],
        rfm: rfmData?.segment ? rfmData : null,
      });
    } catch (e) {
      setQuick((prev) => ({ ...prev, customer: c }));
    }
  };

  const onCustomerUpdated = (updatedCustomer) => {
    setQuick((prev) => (prev.open ? { ...prev, customer: updatedCustomer } : prev));
    setRows((prev) =>
      prev.map((r) => (r.uid === updatedCustomer.uid ? { ...r, ...updatedCustomer } : r))
    );
  };

  const onCreateCustomer = async () => {
    setCreateErr('');
    setSaving(true);

    try {
      if (!createForm.name || createForm.name.trim() === '') {
        setCreateErr('Le nom est obligatoire');
        setSaving(false);
        return;
      }

      const payload = {
        name: createForm.name.trim(),
        phone: createForm.phone?.trim() || null,
        email: createForm.email?.trim() || null,
        type: createForm.type || 'particulier',
        is_active: !!createForm.is_active
      };

      await apiFetch('/api/customers', {
        method: 'POST',
        json: payload
      });

      setCreateModal(false);
      setCreateForm({
        name: '',
        phone: '',
        email: '',
        type: 'particulier',
        is_active: true
      });
      setCreateErr('');
      await load(1);
    } catch (e) {
      setCreateErr(e?.message || 'Erreur création client');
    } finally {
      setSaving(false);
    }
  };

  const phonesMissing = useMemo(
    () => rows.filter((r) => !r.phone || String(r.phone).trim() === "").length,
    [rows]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Users className="h-4 w-4" />
                <span>Gestion</span>
                <span className="text-slate-300">/</span>
                <span className="font-medium text-slate-900">Clients</span>
              </div>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Clients & Fidelite
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Recherche, paiements a echeance, vue detail.
              </p>
            </div>

            <div className="flex items-center gap-3">
              {newInactive.count > 0 && (
                <button
                  onClick={() => setOpenInactiveModal(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                >
                  <Bell className="h-4 w-4" />
                  {newInactive.count} inactifs (24h)
                </button>
              )}
              <button
                onClick={() => setCreateModal(true)}
                className="inline-flex items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                <Users className="h-4 w-4" />
                Nouveau client
              </button>
              <button
                onClick={onExport}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
              >
                <Download className="h-4 w-4" />
                Export
              </button>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">
                <Upload className="h-4 w-4" />
                Importer
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  onChange={(e) => handleImport(e.target.files[0])}
                />
              </label>
            </div>
          </div>

          {/* KPIs */}
          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <KpiCard icon={Users} label="Total clients" value={total || "—"} sub="En base" />
            <KpiCard icon={CheckCircle} label="Page actuelle" value={rows.length} sub={`Sur ${perPage}`} />
            <KpiCard
              icon={Phone}
              label="Phones vides"
              value={phonesMissing}
              sub="Cette page"
              tone={phonesMissing ? "orange" : "green"}
            />
            <KpiCard icon={Clock} label="Pages" value={`${page} / ${pageCount}`} sub="Navigation" />
          </div>

          {/* Onglets */}
          <div className="mt-6 flex gap-1 border-b border-slate-200">
            {[
              { key: 'list',  label: 'Liste clients' },
              { key: 'aging', label: 'Retards de paiement' },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cx(
                  'px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
                  tab === t.key
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'list' && <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher nom, telephone, email..."
                className="w-full rounded-2xl border border-slate-200 bg-white px-10 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
              />
              {q && (
                <button
                  onClick={() => setQ("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-slate-400 hover:text-slate-600" />
                </button>
              )}
            </div>

            <div className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2.5">
              <SlidersHorizontal className="h-4 w-4 text-slate-500" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="bg-transparent text-sm text-slate-900 outline-none"
              >
                <option value="all">Tous</option>
                <option value="top">Plus de commandes</option>
                <option value="recent">Dernier achat</option>
                <option value="due">Paiements a echeance</option>
                <option value="active">Actifs</option>
                <option value="inactive">Inactifs</option>
              </select>
            </div>

            <button
              onClick={() => load(page)}
              disabled={loading}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rafraichir"}
            </button>

            <button
              onClick={() => {
                const newMode = viewMode === 'grid' ? 'table' : 'grid'
                setViewMode(newMode)
                localStorage.setItem('customers_view_mode', newMode)
              }}
              className={cx(
                'rounded-2xl border px-4 py-2.5 text-sm font-semibold transition',
                viewMode === 'table'
                  ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-950'
                  : 'bg-white text-slate-900 border-slate-200 hover:bg-slate-50'
              )}
            >
              {viewMode === 'grid' ? '📊 Table' : '🔲 Grille'}
            </button>
          </div>}
        </div>
      </div>

      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 py-6">
        {tab === 'aging' && <AgingReport />}
        {tab === 'list' && <>
        {errMsg && (
          <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {errMsg}
          </div>
        )}

        {loading ? (
          <SkeletonGrid />
        ) : rows.length === 0 ? (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50">
              <Users className="h-7 w-7 text-slate-600" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">Aucun client trouve</h3>
            <p className="mt-1 text-sm text-slate-600">
              Ajuste ta recherche ou tes filtres.
            </p>
          </div>
        ) : viewMode === 'table' ? (
          <CustomersTable
            customers={rows}
            openId={openId}
            setOpenId={setOpenId}
            onCustomerUpdated={onCustomerUpdated}
            saving={saving}
            setSaving={setSaving}
            dueTone={dueTone}
            rfmMap={rfmMap}
          />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {rows.map((c) => (
              <button
                key={c.uid}
                type="button"
                onClick={() => openQuick(c)}
                className="rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-lg font-bold text-slate-900 truncate">
                      {c.name || "—"}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                      <Phone className="h-3 w-3" />
                      {c.phone || "—"}
                      {c.email && (
                        <>
                          <span className="text-slate-300">·</span>
                          <Mail className="h-3 w-3" />
                          {c.email}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="text-right flex flex-col items-end gap-1">
                    <Pill tone={c.is_active ? "green" : "red"}>
                      {c.is_active ? "Actif" : "Inactif"}
                    </Pill>
                    {rfmMap[c.uid]?.segment && (() => {
                      const RFM_SEG = {
                        champion:   { label: 'Champion',   emoji: '🥇', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
                        loyal:      { label: 'Fidèle',     emoji: '🥈', cls: 'bg-blue-50 text-blue-700 ring-blue-100'          },
                        new:        { label: 'Nouveau',    emoji: '🌱', cls: 'bg-violet-50 text-violet-700 ring-violet-100'    },
                        at_risk:    { label: 'À risque',   emoji: '⚠️', cls: 'bg-amber-50 text-amber-700 ring-amber-100'       },
                        occasional: { label: 'Occasion.',  emoji: '🔄', cls: 'bg-slate-100 text-slate-700 ring-slate-200'      },
                        lost:       { label: 'Perdu',      emoji: '💤', cls: 'bg-rose-50 text-rose-700 ring-rose-100'          },
                      }
                      const seg = RFM_SEG[rfmMap[c.uid].segment] || RFM_SEG.occasional
                      return (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ${seg.cls}`}>
                          {seg.emoji} {seg.label}
                        </span>
                      )
                    })()}
                    <div className="text-xs text-slate-500">
                      Cmd: <span className="font-semibold text-slate-900">{c.total_orders ?? 0}</span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  {c.has_deferred_payment && c.next_payment_due_at && (
                    <Pill tone={dueTone(c.next_payment_due_at)}>
                      <Bell className="h-3 w-3" />
                      {new Date(c.next_payment_due_at).toLocaleDateString("fr-FR")}
                    </Pill>
                  )}

                  {c.has_deferred_payment && Number(c.remaining_total || 0) > 0 && (
                    <Pill tone="blue">
                      Reste: {Number(c.remaining_total).toLocaleString("fr-FR")} FCFA
                    </Pill>
                  )}

                  {(!c.phone || String(c.phone).trim() === "") && (
                    <Pill tone="orange">
                      <AlertCircle className="h-3 w-3" />
                      Tel. manquant
                    </Pill>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pageCount > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Page {page} sur {pageCount}
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => onPage(page - 1)}
                disabled={page <= 1}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft className="h-4 w-4" />
                Precedent
              </button>
              <button
                onClick={() => onPage(page + 1)}
                disabled={page >= pageCount}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
        </>}
      </div>

      {quick.open && (
        <QuickViewModal
          open={quick.open}
          onClose={() => setQuick({ open: false, customer: null, orders: [], rfm: null })}
          customer={quick.customer}
          orders={quick.orders}
          rfm={quick.rfm}
          onCustomerUpdated={onCustomerUpdated}
        />
      )}

      <NewInactiveModal
        open={openInactiveModal}
        onClose={() => setOpenInactiveModal(false)}
        items={newInactive.customers}
      />

      {createModal && (
        <ModalShell
          title="Nouveau client"
          subtitle="Ajouter un client à la base"
          onClose={() => {
            setCreateModal(false);
            setCreateErr('');
            setCreateForm({
              name: '',
              phone: '',
              email: '',
              type: 'particulier',
              is_active: true
            });
          }}
        >
          <div className="space-y-4">
            {createErr && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
                {createErr}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Nom *</div>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="Nom du client"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Téléphone</div>
                <input
                  type="tel"
                  value={createForm.phone}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="+225..."
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Email</div>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                  placeholder="email@exemple.com"
                />
              </div>

              <div>
                <div className="text-xs font-semibold text-slate-500 mb-1">Type</div>
                <select
                  value={createForm.type}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, type: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100"
                >
                  <option value="particulier">Particulier</option>
                  <option value="professionnel">Professionnel</option>
                  <option value="entreprise">Entreprise</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={createForm.is_active}
                    onChange={(e) => setCreateForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  <span className="text-sm font-medium text-slate-900">Client actif</span>
                </label>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <button
                disabled={saving}
                onClick={() => {
                  setCreateModal(false);
                  setCreateErr('');
                  setCreateForm({
                    name: '',
                    phone: '',
                    email: '',
                    type: 'particulier',
                    is_active: true
                  });
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50 disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                disabled={saving}
                onClick={onCreateCustomer}
                className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                {saving ? 'Création...' : 'Créer client'}
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
